import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchAbilities, fetchAbilityEvidence, fetchPracticeLog, fetchProgress, fetchRecords, resetProgress, submitAbilityEvidence, loc } from "@/lib/ethicsApi";
import EthicsFrame from "./EthicsFrame";
import { useT } from "@/i18n/LanguageContext";

function computeLevel(ability, progress, logs, evidence, records) {
  if (evidence.some((e) => e.abilityId === ability.id)) return 6;
  const related = progress.filter((p) => ability.lessonSlugs?.includes(p.lessonSlug));
  const practiceHits = logs.filter((l) => (ability.practices || []).includes(l.practiceId)).length;
  const strong = related.filter((r) => ["Practised", "Integrated", "Independent", "Demonstrated"].includes(r.learningStatus)).length;
  if (practiceHits >= 3 && strong) return 5;
  if (practiceHits >= 1 && strong) return 4;
  if (strong) return 3;
  if (related.length) return 2;
  if (progress.length || records.length) return 1;
  return 0;
}

export default function PathOfEthics() {
  const { lang } = useT();
  const L = (v) => loc(v, lang);
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState([]);
  const [logs, setLogs] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [records, setRecords] = useState([]);
  const [course, setCourse] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [msg, setMsg] = useState("");

  useEffect(() => {
    fetchAbilities().then(setData).catch(() => {});
    fetchProgress().then((d) => setProgress(d.records || []));
    fetchPracticeLog().then((d) => setLogs(d.logs || [])).catch(() => {});
    fetchAbilityEvidence().then((d) => setEvidence(d.evidence || [])).catch(() => {});
    fetchRecords().then((d) => setRecords(d.records || [])).catch(() => {});
    import("@/lib/ethicsApi").then((m) => m.fetchCourse().then(setCourse));
  }, []);

  if (!data) return <div className="min-h-screen bg-ink" />;
  const levels = data.levels || [];
  const slugByOrder = {};
  course?.phases?.forEach((p) => p.lessons.forEach((l) => { slugByOrder[l.order] = l.slug; }));
  const abilities = data.abilities.map((a) => ({ ...a, lessonSlugs: (a.lessons || []).map((n) => slugByOrder[n]).filter(Boolean) }));

  const submitEvidence = async (aid) => {
    try {
      await submitAbilityEvidence(aid, drafts[aid] || "");
      setEvidence((e) => [...e.filter((x) => x.abilityId !== aid), { abilityId: aid, evidence: drafts[aid] }]);
      setMsg(lang === "nl" ? "Aangetoond. Het Pad noteert het bewijs." : "Demonstrated. The Path records the evidence.");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) { setMsg(e.message); }
  };

  const doReset = async () => {
    if (!window.confirm(lang === "nl" ? "Alle voortgang van Fysica wissen? Dit raakt Logica niet." : "Erase all Ethics progress? This does not touch Logic.")) return;
    await resetProgress();
    setProgress([]);
    setMsg(lang === "nl" ? "Voortgang gewist." : "Progress erased.");
  };

  const demonstrated = abilities.filter((a) => computeLevel(a, progress, logs, evidence, records) >= 6).length;

  return (
    <EthicsFrame title="The Path of Right Action" crumb="Path" testid="ethics-path-page" canonicalPath="/path">
      <p className="text-fog/80 font-reading max-w-2xl mb-3 leading-[1.9]">
        {lang === "nl"
          ? "Vijftien vermogens, zeven niveaus. Niveaus stijgen door werkelijke activiteit: lessen doorlopen, oefeningen voltooien, praktijken vastleggen. Het hoogste niveau, Demonstrated, vraagt één ding dat geen pagina kan geven: een echt voorbeeld uit jouw leven."
          : "Fifteen capacities, seven levels. Levels rise through genuine activity: walking lessons, completing exercises, recording practices. The highest level, Demonstrated, asks the one thing no page can give: a real example from your life."}
      </p>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <p className="text-xs tracking-[0.2em] uppercase text-[#c86058]" data-testid="ethics-path-summary">{demonstrated} / {abilities.length} demonstrated</p>
        <button onClick={doReset} data-testid="ethics-reset-progress" className="text-[10px] tracking-[0.2em] uppercase text-fog/45 hover:text-red-300">{lang === "nl" ? "Voortgang wissen" : "Reset progress"}</button>
      </div>
      <p aria-live="polite" className="text-[11px] text-gold/80 h-4 mb-4">{msg}</p>

      <div className="space-y-3">
        {abilities.map((a) => {
          const lvl = computeLevel(a, progress, logs, evidence, records);
          const open = openId === a.id;
          return (
            <article key={a.id} className="border border-[#c8a95a]/15 bg-[#14211a]">
              <button onClick={() => setOpenId(open ? null : a.id)} aria-expanded={open} data-testid={`ethics-ability-${a.id}`}
                className="w-full text-left px-5 py-4 focus:outline-none focus:ring-2 focus:ring-gold/60">
                <div className="flex items-center justify-between gap-4">
                  <span className="font-serif text-lg text-bone">{L(a.name)}</span>
                  <span className={`text-[10px] tracking-[0.15em] uppercase whitespace-nowrap ${lvl >= 6 ? "text-gold" : lvl >= 3 ? "text-[#c86058]" : "text-fog/45"}`}>{levels[lvl]}</span>
                </div>
                <div className="mt-3 flex gap-1" aria-hidden>
                  {levels.map((_, i) => (
                    <span key={i} className={`h-1 flex-1 ${i <= lvl && lvl > 0 ? (lvl >= 6 ? "bg-gold/80" : "bg-[#c86058]/70") : "bg-white/10"}`} />
                  ))}
                </div>
              </button>
              {open && (
                <div className="px-5 pb-5 space-y-3">
                  <p className="text-sm text-fog font-reading leading-relaxed">{L(a.meaning)}</p>
                  <p className="text-xs text-[#c86058] font-reading italic">{L(a.grounding)}</p>
                  <div className="grid sm:grid-cols-3 gap-3 text-xs text-fog/75 font-reading">
                    <p><span className="text-fog/45 uppercase tracking-wider text-[9px] block">Beginner</span>{L(a.beginner)}</p>
                    <p><span className="text-fog/45 uppercase tracking-wider text-[9px] block">Advanced</span>{L(a.advanced)}</p>
                    <p><span className="text-fog/45 uppercase tracking-wider text-[9px] block">Mastery</span>{L(a.mastery)}</p>
                  </div>
                  <p className="text-sm text-bone/85 font-reading border-l-2 border-gold/40 pl-3">{L(a.exercise)}</p>
                  <p className="text-[11px] text-fog/50">{lang === "nl" ? "Lessen" : "Lessons"} {(a.lessons || []).join(", ")} · {(a.practices || []).join(", ")}</p>
                  <div className="pt-3 border-t border-white/10">
                    <label htmlFor={`ev-${a.id}`} className="block text-[10px] tracking-[0.2em] uppercase text-gold/70 mb-1.5">
                      {lang === "nl" ? "Toon dit aan met een echt voorbeeld uit je leven" : "Demonstrate this with a real example from your life"}
                    </label>
                    <textarea id={`ev-${a.id}`} rows={3} value={drafts[a.id] || ""} data-testid={`ethics-evidence-${a.id}`}
                      onChange={(e) => setDrafts((d) => ({ ...d, [a.id]: e.target.value }))}
                      className="w-full bg-transparent border border-[#c8a95a]/20 text-bone font-reading p-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
                    <button onClick={() => submitEvidence(a.id)} data-testid={`ethics-evidence-submit-${a.id}`}
                      className="mt-2 px-5 py-2.5 border border-gold/50 text-gold text-[10px] tracking-[0.22em] uppercase hover:bg-gold/10 focus:outline-none focus:ring-2 focus:ring-gold/60">
                      {lang === "nl" ? "Bewijs indienen" : "Submit evidence"}
                    </button>
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>
      <p className="mt-10 text-sm text-fog/60 font-reading">
        {lang === "nl" ? "Het Pad voedt zich uit de " : "The Path feeds on the "}
        <Link to="/academy/ethics/record" className="text-gold hover:underline">Record of Right Action</Link>
        {lang === "nl" ? " en de " : " and the "}
        <Link to="/academy/ethics/practice" className="text-gold hover:underline">Instruments of Practice</Link>.
      </p>
    </EthicsFrame>
  );
}
